import type { ApplyMatchesResult } from '../../services/integrationApi';
import { addApplyMatchesResult, emptyApplyMatchesResult } from './applyMatchesResult';

type MatchRef = { shopifyProductId: string; canonicalProductId: string };

/**
 * Matches per request. The server stops itself before Firebase Hosting's 60s cutoff and returns
 * `nextIndex`; this only keeps one body small. 500 is the previous batch size.
 */
export const APPLY_MATCH_CHUNK = 500;

export interface ApplyMatchProgress {
  done: number;
  total: number;
}

/**
 * One click links every match. Each response covers a slice; a `nextIndex` (or the next chunk)
 * is sent immediately, so a store does not have to click again to get past the first slice.
 * A dropped response is not sent again unless `retryDropped` is set. Only link-only sets it:
 * a row already written is cheap the second time. Replaying enrich or override can POST the same
 * images again while the first call is still running. Throws if a slice cannot be applied;
 * `onSlice` has already reported the slices that finished.
 */
export async function applyMatchesInSlices(args: {
  matches: MatchRef[];
  apply: (slice: MatchRef[]) => Promise<ApplyMatchesResult>;
  onProgress?: (progress: ApplyMatchProgress) => void;
  onSlice?: (soFar: ApplyMatchesResult, done: number) => void;
  /** Link-only only. Off by default so a content update is never replayed automatically. */
  retryDropped?: boolean;
}): Promise<ApplyMatchesResult> {
  const { matches, apply, onProgress, onSlice, retryDropped = false } = args;
  const attempts = retryDropped ? 2 : 1;
  let cursor = 0;
  let soFar = emptyApplyMatchesResult();
  onProgress?.({ done: 0, total: matches.length });
  while (cursor < matches.length) {
    const slice = matches.slice(cursor, cursor + APPLY_MATCH_CHUNK);
    let part: ApplyMatchesResult | null = null;
    let lastError: unknown;
    for (let attempt = 0; attempt < attempts; attempt++) {
      try {
        part = await apply(slice);
        break;
      } catch (error) {
        lastError = error;
      }
    }
    if (!part) throw lastError instanceof Error ? lastError : new Error('Could not apply the matches.');
    const reported = part.nextIndex == null ? slice.length : part.nextIndex;
    const consumed = Math.min(slice.length, Math.max(0, reported));
    // nextIndex 0 means the server started nothing. Retrying the same slice would spin.
    if (consumed <= 0) throw new Error('Could not apply the matches.');
    soFar = addApplyMatchesResult(soFar, part);
    cursor += consumed;
    const done = Math.min(cursor, matches.length);
    onProgress?.({ done, total: matches.length });
    onSlice?.(soFar, done);
  }
  return soFar;
}
